// Canonical LSEG Excel-add-in cell texts and the error -> cell text mapping.
// Strings are pinned to the golden captures (docs/lseg-golden). No imports from
// api.ts: ApiError is duck-typed on {status, detail} so this stays node-importable.

export const LSEG_TEXT = {
  UNRESOLVED: "Unable to resolve all requested identifiers.",
  NO_UNIVERSE: "No universe defined.",
  // TR with an empty Data Item (golden a_tr_empty_fields).
  NO_DATA_ITEM: "No data item defined.",
  DENIED: "The access to field(s) denied.",
  INVALID_IDENTIFIER_IGNORED: "The invalid identifier ignored.",
  INVALID_FIELDS: "Invalid field(s).",
  NOT_SIGNED_IN: "Not Signed In",
  RT_NOT_FOUND: "#N/A The record could not be found",
  // A realtime field (CF_BID, BID…) of an unknown RIC inside TR (golden
  // tr_cf_bid_unknown_ric). Not RtGet's text, nor TR.*'s.
  RT_RECORD_NOT_FOUND_TR: "The record could not be found (Error code: 0)",
  RT_NO_FIELD: "#N/A No field defined.",
  // RtGet with a field list ("BID;ASK"): one field LSEG does not know (golden o_rtget_two_fields).
  RT_INVALID_FIELD: "#N/A Invalid field.",
  RT_STALE: "#N/A Stale",
  CONNECTION_LOST: "Connection was lost. Please log out and log in again.",
  // A multi-RIC RHistory shows this in the failed RIC's column (golden rh_one_good_one_bad C2).
  INVALID_RIC: "Invalid RIC.",
  // RHistory over a window with no rows (golden rh_valid_ric_no_rows).
  NA: "#N/A",
  // RHistory with an unsigned relative END (golden x_rh_end_plus5d: END:5D).
  REQUEST_VALIDATION: "Request Validation Error",
  // A null TR DataGrid cell with no per-cell error (golden tr_far_period_empty).
  NULL: "NULL",
} as const;

export const rowFieldError = (field: string) =>
  `Unable to collect data for the field '${field}' and some specific identifier(s).`;
// Separator ", " pinned to golden rh_two_bad_rics B2.
export const invalidRics = (rics: string[]) => `Invalid RIC(s): ${rics.join(", ")}`;
export const invalidDisplayParam = (key: string) => `${key} is not a valid display parameter.`;
// RHistory request key LSEG does not know (goldens opt_rh_bad_request_key FOO, o_rh_day DAY).
export const invalidRequestParam = (key: string) => `${key} is not a valid request parameter.`;
// RHistory Refresh argument LSEG rejects (goldens rh_refresh_abs_1 `1`, rh_refresh_abs_y `Y`).
export const invalidRefreshParam = (word: string) => `${word} is not a valid refresh parameter.`;
// RHistory option with a value LSEG rejects (goldens opt_rh_adjusted0, opt_rh_interval_1h,
// o_rh_source, o_rh_null_display, o_rh_end_relative).
export const invalidValueFor = (key: string) => `Invalid value for ${key}.`;
// TR option written with no value (golden opt_tr_null_blank: `NULL=`).
export const noValueForParam = (key: string) => `No value provided for parameter ${key}`;

export type LsegFamily = "TR" | "RHISTORY" | "RTGET";

const UNRESOLVED_PREFIX = "Unable to resolve all requested identifiers";
// get_history's word for an unknown RIC ("No data to return, ... (TS.Interday.
// UserRequestError.70005, The universe is not found)"): same meaning.
const UNIVERSE_NOT_FOUND = "The universe is not found";

const isNotFoundMessage = (message: string) =>
  message.startsWith(UNRESOLVED_PREFIX) || message.includes(UNIVERSE_NOT_FOUND);

/** An LSEG business error forwarded by the backend (HTTP 422, detail.kind "lseg"). */
export function isLsegError(err: unknown): boolean {
  const e = err as { status?: unknown; detail?: any } | null | undefined;
  const d = e?.detail;
  return !!e && e.status === 422 && !!d && !Array.isArray(d) && d.kind === "lseg" && typeof d.message === "string";
}

/** An LSEG 422 saying one or more identifiers could not be resolved (or, for history, were not found). */
export function isUnresolvedError(err: unknown): boolean {
  return isLsegError(err) && isNotFoundMessage(String((err as any).detail.message));
}

// LSEG 422 messages that are about the formula itself, not any RIC: every
// subset of a batch fails the same way, so the batcher does not bisect them.
// Deliberately narrow: every other LSEG 422 is bisected, since it may hold
// for some RICs only ("The universe does not support the following fields"
// for Xetra's missing TRDPRC_1 history, a per-RIC entitlement "denied", an
// unresolved identifier).
const FORMULA_LEVEL_MESSAGES = ["Invalid field", "not a valid display parameter"];

/** True for an LSEG 422 that fails the whole formula whatever the RICs (see FORMULA_LEVEL_MESSAGES). */
export function isFormulaLevelError(err: unknown): boolean {
  if (!isLsegError(err)) return false;
  const message = String((err as any).detail.message);
  return FORMULA_LEVEL_MESSAGES.some((m) => message.includes(m));
}

/**
 * An error saying the universe does not have the requested history fields
 * (TSCC.QS.UserRequestError.90006, "The universe does not support the
 * following fields: [OFF_CLOSE]"), wherever its text sits (an LSEG 422's
 * detail, or the message of another error).
 */
export function isUnsupportedFieldsError(err: unknown): boolean {
  const e = err as { detail?: any; message?: unknown } | null | undefined;
  if (!e) return false;
  const text = `${e.detail && typeof e.detail.message === "string" ? e.detail.message : ""} ${typeof e.message === "string" ? e.message : ""}`;
  return /does not support the following fields/i.test(text);
}

// RICs listed in "... identifiers in ['A.N', 'B.N']." (empty if none).
function parseRics(message: string): string[] {
  const list = /\[([^\]]*)\]/.exec(message);
  if (!list) return [];
  const out: string[] = [];
  const re = /['"]([^'"]+)['"]/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(list[1]))) out.push(m[1]);
  return out;
}

// The LSEG library appends the request to some messages ("The access to
// field(s) denied. Requested universes: ['ACCP.PA']. Requested fields:
// ['TR.DPS']"); LSEG Excel shows only its own sentence (golden tr_dps_denied).
const REQUEST_SUFFIXES = [" Requested universes:", " Requested fields:"];

/** An LSEG message without the library's "Requested universes/fields" suffix. */
export function trimLsegMessage(message: string): string {
  const cut = REQUEST_SUFFIXES.map((s) => message.indexOf(s)).filter((i) => i >= 0);
  return cut.length ? message.slice(0, Math.min(...cut)) : message;
}

export function errorToCellText(err: unknown, family: LsegFamily, rics?: string[]): string {
  const e = err as { status?: unknown; detail?: any } | null | undefined;
  const status = typeof e?.status === "number" ? e.status : undefined;

  if (status === 401) {
    return family === "RTGET" ? LSEG_TEXT.RT_STALE : LSEG_TEXT.NOT_SIGNED_IN;
  }

  if (isLsegError(err)) {
    const message = trimLsegMessage(e!.detail.message);
    if (isNotFoundMessage(message)) {
      if (family === "RTGET") return LSEG_TEXT.RT_NOT_FOUND;
      if (family === "RHISTORY") {
        const list = rics && rics.length ? rics : parseRics(message);
        return list.length ? invalidRics(list) : LSEG_TEXT.UNRESOLVED;
      }
      return LSEG_TEXT.UNRESOLVED;
    }
    return message;
  }

  return LSEG_TEXT.CONNECTION_LOST;
}

// How a refused search reaches the add-in: lseg-data's LDError prints as
// "Error code <code> | <message>", and the hub (which has no marker for search
// errors) wraps it as a 502 "Upstream error: Error code 400 | Invalid filter: ...",
// which the interface forwards as {status: 502, detail: {kind: "hub", message}}.
// Only an LSEG 400 is the user's criteria. Any other 4xx (a 404 is a missing
// endpoint, never the criteria), a 502 with no code, "Error code 5NN | ...", a
// timeout or the interface's own "network error: ..." is not LSEG's answer.
const UPSTREAM_PREFIX = /^Upstream error:\s*/i;
const ERROR_4XX_PREFIX = /^Error code 4\d\d\s*\|\s*/i;
const ERROR_400_PREFIX = /^Error code 400\s*\|\s*/i;

/**
 * The service's own message when it refused a search over its criteria
 * (golden srch_badfield: `Invalid filter: unknown property 'NotAField'`), else
 * null. It comes from:
 *  - an LSEG 422 (kind "lseg"), its "Error code 4NN | " head stripped and its
 *    end trimmed (a leading space stays: " Universe is not resolved");
 *  - a hub 502 whose text, after "Upstream error: ", starts "Error code 400 | ".
 * The exact search message is to be confirmed live (golden srch_badfield).
 */
export function searchRefusal(err: unknown): string | null {
  const e = err as { status?: unknown; detail?: any } | null | undefined;
  const d = e?.detail;
  // Only the end is trimmed: a message may start with a space that LSEG shows (" Universe is not resolved").
  if (isLsegError(err)) return trimLsegMessage(d.message).replace(ERROR_4XX_PREFIX, "").replace(/\s+$/, "") || null;
  if (e?.status === 502 && d && !Array.isArray(d) && d.kind === "hub" && typeof d.message === "string") {
    const text = d.message.replace(UPSTREAM_PREFIX, "");
    if (ERROR_400_PREFIX.test(text)) return text.replace(ERROR_400_PREFIX, "").trim() || null;
  }
  return null;
}

/**
 * The text a failed RDP.Search shows: the service's message for a refusal
 * (searchRefusal), else errorToCellText (sign-in, network, busy, outages).
 */
export function searchFailureText(err: unknown): string {
  return searchRefusal(err) ?? errorToCellText(err, "TR");
}
